import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import createAxios from "@/lib/axios";
import { toast } from "sonner";
import { EditIcon, ExternalLinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormEvent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import Markdown from "@/components/Markdown";
import { Badge } from "@/components/ui/badge";
import { usePeriodicRefresh } from "@/hooks/usePeriodicRefresh";

type AnswerSheet = {
  id: string;
  name: string;
  progress: {
    finished: number;
    total: number;
    error: number;
  };
  file_path: string;
  marks_obtained: number;
};

type EvaluationEntry = {
  _id: string;
  answer_sheet_id: string;
  question: string;
  question_num: number;
  obtained_marks: number;
  created_at: string;
  last_updated: string;
  feedback: string;
  reference_answer: string;
  marks: number;
  rubrics: string;
  response: string;
};

type AnswerSheetData = {
  answer_sheet: AnswerSheet;
  entries: EvaluationEntry[];
};

function getUrl(filePath: string) {
  return `https://storage.googleapis.com/input-aae2e1fa3a5548a/${filePath}`;
}

function PDFViewer({ url }: { url: string }) {
  return (
    <object
      data={url}
      type="application/pdf"
      width="100%"
      height="100%"
      className="size-full rounded-lg border-4"
    >
      <div className="flex flex-col items-center gap-2 text-gray-700">
        <p>Error: Unable to display PDF file.</p>
        <a
          href={url}
          target="_blank"
          className="flex flex-row items-center gap-2 text-white underline"
        >
          <span>Download</span> <ExternalLinkIcon className="size-4" />
        </a>
      </div>
    </object>
  );
}

function EditAnswerCard({
  evaluationEntry,
}: {
  evaluationEntry: EvaluationEntry;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState<{
    response: string;
    obtained_marks: string;
    feedback: string;
  }>({
    response: "",
    obtained_marks: "",
    feedback: "",
  });
  const [isEdited, setIsEdited] = useState(false);
  const [response, setResponse] = useState("");
  const navigate = Route.useNavigate();

  console.log(response);

  const entryData = evaluationEntry;

  useEffect(() => {
    if (entryData) {
      setFormData({
        response: entryData.response || "",
        obtained_marks: entryData.obtained_marks.toString() || "",
        feedback: entryData.feedback || "",
      });
      setResponse(entryData.response || "");
    }
  }, [entryData, evaluationEntry]);

  if (!entryData) {
    return (
      <Card className="p-4">
        <CardContent>No evaluation data available</CardContent>
      </Card>
    );
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsEdited(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const requestBody = {
        response: formData.response,
        obtained_marks: parseInt(formData.obtained_marks) || 0,
        feedback: formData.feedback,
      };
      const id = evaluationEntry._id;

      const client = await createAxios();
      const response = await client.post(
        `${import.meta.env.VITE_BACKEND_URL}/evaluation-entry/${id}/edit`,

        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Evaluation updated successfully");
      setIsEdited(false);
      setShowDialog(false);

      navigate({
        to: "/dashboard/evaluate/answer-script/$id",
        params: { id: entryData.answer_sheet_id },
      });
      return response.data;
    } catch (error) {
      toast.error("Failed to update evaluation");
      throw error;
    }
  };

  const dataStr = `
# Question ${entryData.question_num + 1}
${entryData.question}

---

## Reference Answer
${entryData.reference_answer}

---

## Rubrics
${entryData.rubrics}

---

## User Response
${entryData.response}

---

## Reasoning 
${entryData.feedback}

---
`;
  return (
    <Card className="mb-4 border-2 py-4">
      <CardContent>
        <div className="flex flex-col space-y-2">
          <Markdown>{dataStr}</Markdown>
          <div className="flex items-center justify-start gap-4">
            <Label className="text-nowrap text-xl font-bold">
              Obtained Marks:
            </Label>
            <div className="flex-grow">
              <Badge className="text-base">
                {entryData.obtained_marks}/{entryData.marks}
              </Badge>
            </div>
            <Button
              onClick={() => setShowDialog(true)}
              className="flex flex-row items-center gap-4"
            >
              <EditIcon className="size-4" />
              <span>Edit</span>
            </Button>
          </div>
        </div>
      </CardContent>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl rounded-lg bg-white text-black shadow-lg">
          <DialogHeader>
            <DialogTitle>Answer Details</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <form onSubmit={handleSubmit}>
              <CardContent>
                <div>
                  <div className="grid gap-4 gap-y-2">
                    <div className="grid gap-2">
                      <Label className="text-sm font-medium">Response:</Label>
                      <Textarea
                        name="response"
                        value={formData.response || ""}
                        onChange={handleInputChange}
                        className="h-48 w-full bg-gray-200"
                        autoFocus
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label className="text-sm font-medium">
                        Obtained Marks:
                      </Label>
                      <Input
                        name="obtained_marks"
                        type="number"
                        value={formData.obtained_marks}
                        onChange={handleInputChange}
                        className="w-full bg-gray-200"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label className="text-sm font-medium">Reasoning:</Label>
                      <Textarea
                        name="feedback"
                        value={formData.feedback || ""}
                        onChange={handleInputChange}
                        className="h-36 w-full bg-gray-200"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <div className="flex justify-end gap-2 p-4">
                <DialogClose asChild>
                  <Button variant="outline2" type="button">
                    Cancel
                  </Button>
                </DialogClose>
                {isEdited && (
                  <Button
                    type="submit"
                    variant="default"
                    className="bg-green-500"
                  >
                    Submit
                  </Button>
                )}
              </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ViewAnswerScript() {
  usePeriodicRefresh(10_000);
  const { evaluationEntries } = Route.useLoaderData();
  const { answerSheet } = Route.useLoaderData();
  const { pdfUrl } = Route.useLoaderData();

  const totalMarks = answerSheet?.totalMarks || 0;

  const progressPercentage = answerSheet?.progress
    ? Math.round(
        (answerSheet.progress.finished / answerSheet.progress.total) * 100,
      )
    : 0;

  return (
    <div className="light flex h-full min-h-screen w-full flex-col">
      <Card className="m-4 flex-1">
        <CardHeader>
          <CardTitle>View Answer Script: {answerSheet.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 p-2">
            <div className="flex justify-between gap-2">
              {[
                {
                  name: "Obtained Marks",
                  value: answerSheet.marks_obtained,
                },
                { name: "Total Marks", value: totalMarks },
                { name: "Progress", value: answerSheet.progress.finished },
                { name: "Errors", value: answerSheet.progress.error },
                { name: "Total", value: answerSheet.progress.total },
              ].map(({ name, value }) => (
                <div key={name} className="flex flex-col items-start gap-2">
                  <p className="text-sm font-bold">{name}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
              ))}
            </div>
            <div className="gap-2 space-y-2 py-2">
              <Progress value={progressPercentage} className="w-full" />
              <div className="text-sm text-muted-foreground">
                <span>Progress: </span>
                {answerSheet.progress.finished} of {answerSheet.progress.total}{" "}
                completed
                {answerSheet.progress.error > 0 &&
                  ` (${answerSheet.progress.error} errors)`}
              </div>
            </div>
          </div>

          <div className="flex flex-row items-start gap-8">
            <div className="flex h-[70vh] w-full flex-1">
              {pdfUrl ? (
                <PDFViewer url={pdfUrl} />
              ) : (
                <p>No answer script available</p>
              )}
            </div>
            <ScrollArea className="h-[70vh] w-1/2">
              {evaluationEntries?.map((evaluationEntry) => (
                <EditAnswerCard evaluationEntry={evaluationEntry} />
              ))}
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

async function GetAnswerSheetDataById(id: string): Promise<AnswerSheetData> {
  const client = await createAxios();
  const response = await client.get(
    `${import.meta.env.VITE_BACKEND_URL}/answer-sheet/${id}`,
  );
  return response.data;
}

export const Route = createFileRoute("/dashboard/evaluate/answer-script/$id")({
  component: ViewAnswerScript,
  loader: async ({ params: { id } }) => {
    const answerSheetData = await GetAnswerSheetDataById(id);
    const answerSheet = answerSheetData.answer_sheet;
    const evaluationEntries = answerSheetData.entries;

    const totalMarks = evaluationEntries.reduce(
      (sum, entry) => sum + entry.marks,
      0,
    );

    const pdfUrl = answerSheet ? getUrl(answerSheet.file_path!) : "";
    return {
      answerSheet: { ...answerSheet, totalMarks },
      evaluationEntries,
      pdfUrl,
      id,
      refetchInterval: 1000,
    };
  },
});
